// src/pages/Home.jsx
import React, { useEffect, useMemo, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../axiosInstance";
import { SocieteContext } from "../context/SocieteContext";
import {
  ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  PieChart, Pie, Cell
} from "recharts";
import TrialBanner from "../components/TrialBanner";
import ApiErrorBanner from "../components/ApiErrorBanner";
/* ================= Helpers ================= */
const fmt2 = (n) =>
  Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money = (n) =>
  `${Number(n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0, minimumFractionDigits: 0 })} €`;

const dFR = (raw) => {
  if (!raw) return "—";
  try {
    let s;

    if (typeof raw === "string") {
      // cas "2025-12-08T10:15:00.000Z" ou "2025-12-08"
      s = raw.slice(0, 10);
    } else {
      const d = new Date(raw);
      if (!d || Number.isNaN(d.getTime())) {
        return "—";
      }
      s = d.toISOString().slice(0, 10);
    }

    const parts = s.split("-");
    if (parts.length !== 3) return "—";

    const [y, m, d] = parts;
    if (!y || !m || !d) return "—";

    return `${d}/${m}/${y}`;
  } catch (e) {
    console.warn("dFR – date invalide:", raw, e);
    return "—";
  }
};


const clampNb = (v) => (Number.isFinite(v) ? v : 0);
const toNum = (v) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (typeof v === "string") {
    // "1 300,50", "1 300,50", "1300,50", "1300.50"
    const s = v.replace(/\s| /g, "").replace(",", ".");
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};
const toPct = (v, fallback = 20) => {
  if (v == null) return fallback;
  if (typeof v === "string") {
    const s = v.replace("%", "").trim();
    const n = Number(s);
    return Number.isFinite(n) ? n : fallback;
  }
  return Number.isFinite(v) ? v : fallback;
};
// --- Factures : statut (gris/bleu/rouge)
const parseMeta = (m) => {
  if (!m) return null;
  try { return typeof m === "string" ? JSON.parse(m) : m; } catch { return null; }
};

const getDueDate = (f) => {
  const meta = parseMeta(f.meta);
  // priorité: meta.date_echeance -> date + delai (30 par défaut)
  const d0 = new Date(f.date || f.created_at || Date.now());
  const delai = Number(meta?.delai_paiement_jours ?? 30);
  const dd = meta?.date_echeance ? new Date(meta.date_echeance) : new Date(d0);
  if (!meta?.date_echeance) dd.setDate(dd.getDate() + (Number.isFinite(delai) ? delai : 30));
  return dd;
};

const isOverdue = (f, today = new Date()) => !isPaid(f) && getDueDate(f) < today;

const statusOfFacture = (f, today = new Date()) => {
  if (isPaid(f)) return "accepted";      // bleu (payées)
  if (isOverdue(f, today)) return "abandoned"; // rouge (en retard)
  return "pending";                      // gris (en attente de paiement)
};

// extrait toujours un tableau, quelle que soit la forme du payload
const extractArray = (payload, ...keys) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  for (const k of keys) {
    if (Array.isArray(payload[k])) return payload[k];
  }
  return [];
};

const normalize = (v = "") =>
  String(v).normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase().trim();
const statusOfDevis = (d) => {
  const s = normalize(d.etat ?? d.statut ?? "");
  if (/accept/.test(s)) return "accepted";
  if (/abandon|refus|rejet/.test(s)) return "abandoned";
  // “en cours / en attente / brouillon / vide” -> pending
  return "pending";
};

const isPaid = (f = {}) => {
  const etat = normalize(f.etat ?? f.status ?? "");
  if (/(payee|paye|paid|reglee|regle)/.test(etat)) return true;
  if (f.paid_at) return true;
  return false;
};

/* ---------- Date bucketing ---------- */
const monthKey = (dateLike) => {
  const d = new Date(dateLike || Date.now());
  if (Number.isNaN(d.getTime())) return "0000-00";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const monthLabelShort = (key) => {
  const [y, m] = key.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return new Intl.DateTimeFormat("fr-FR", { month: "short" }).format(d);
};
const buildLastMonthKeys = (n, from = new Date()) => {
  const keys = [];
  const start = new Date(from.getFullYear(), from.getMonth() - (n - 1), 1);
  for (let i = 0; i < n; i++) {
    const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
    keys.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return keys; // du plus ancien au plus récent
};

/* ---------- Coûts & marges (HT) robustes ---------- */
// TTC le plus robuste possible
const getFactureTTC = (f = {}) =>
  toNum(f.total_ttc ?? f.montant_ttc ?? f.ttc ?? f.total ?? 0);

// HT robuste (si pas de HT, on convertit le TTC avec TVA)
const getFactureHT = (f = {}) => {
  const ht = toNum(f.total_ht ?? f.montant_ht ?? f.ht);
  if (ht) return ht;
  const ttc = getFactureTTC(f);
  const tva = toPct(f.tva, 20);
  return ttc / (1 + tva / 100);
};

// Coût HT total : champ direct > somme des lignes > meta > 0
const getCostHT = (f = {}) => {
  if (f.cout_ht_total != null) return toNum(f.cout_ht_total);
  const lignes = Array.isArray(f.lignes) ? f.lignes : [];
  if (lignes.length) {
    let sum = 0;
    for (const l of lignes) {
      const q = toNum(l.quantite ?? l.qty ?? 1);
      const tva = toPct(l.tva ?? f.tva ?? 20);
      const coutHT =
        l.cout_ht != null
          ? toNum(l.cout_ht)
          : l.cout_ttc != null
            ? toNum(l.cout_ttc) / (1 + tva / 100)
            : l.meta && l.meta.cout != null
              ? toNum(l.meta.cout)
              : 0;
      sum += q * coutHT;
    }
    if (sum > 0) return sum;
  }
  return 0;
};

const getChargesVariablesHT = (f = {}) =>
  toNum(f.charges_variables_ht ?? f.frais_variables_ht ?? 0);

const getFraisFixesAllouesHT = (f = {}) =>
  toNum(f.frais_fixes_alloues_ht ?? f.allocation_frais_fixes_ht ?? 0);

// Niveaux de marge (toujours HT)
const computeMarginsHT = (f = {}) => {
  const ht = getFactureHT(f);
  const cost = getCostHT(f);
  const varCharges = getChargesVariablesHT(f);
  const fixedAlloc = getFraisFixesAllouesHT(f);
  const brute = ht - cost;
  const ope = brute - varCharges;
  const nette = ope - fixedAlloc;
  return { ht, brute, ope, nette };
};

/* ================= Charts (Recharts) ================= */

const TooltipMoney = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded border bg-white px-2 py-1 text-xs shadow">
      <div className="font-medium">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex gap-2">
          <span>{p.name}:</span>
          <span className="font-semibold">{money(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

function RevenueLineDual({ data, showMargin }) {
  // nombres pleins avec espaces de milliers + symbole €
  const fmtYAxis = (v) =>
    `${Number(v || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0, minimumFractionDigits: 0 })} €`;

  return (
    <ResponsiveContainer width="100%" height={340}>
      <LineChart data={data} margin={{ top: 24, right: 24, bottom: 0, left: 12 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="label" />
        <YAxis tickFormatter={fmtYAxis} width={80} />
        <Tooltip
          content={
            <TooltipMoney />
          }
        />
        <Legend />
        <Line
          type="monotone"
          dataKey="ca"
          name="CA"
          stroke="#2563EB"
          dot={false}
          strokeWidth={2}
          isAnimationActive={false}
        />
        {showMargin && (
          <Line
            type="monotone"
            dataKey="marge"
            name="Marge"
            stroke="#10B981"
            dot={false}
            strokeWidth={2}
            isAnimationActive={false}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
}

function RangeToolbar({ mode, setMode, start, setStart, end, setEnd }) {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-2 text-xs">
      <div className="flex rounded border overflow-hidden">
        <button
          className={`px-2 py-1 ${mode === "month" ? "bg-sky-700 text-white" : "bg-white"}`}
          onClick={() => setMode("month")}
          title="Mois en cours"
        >
          Mois
        </button>
        <button
          className={`px-2 py-1 ${mode === "year" ? "bg-sky-700 text-white" : "bg-white"}`}
          onClick={() => setMode("year")}
          title="Année en cours"
        >
          Année
        </button>
        <button
          className={`px-2 py-1 ${mode === "custom" ? "bg-sky-700 text-white" : "bg-white"}`}
          onClick={() => setMode("custom")}
          title="Période personnalisée"
        >
          Perso
        </button>
      </div>

      {mode === "custom" && (
        <div className="flex items-center gap-2">
          <input type="date" className="border rounded px-2 py-1"
            value={start} onChange={(e) => setStart(e.target.value)} />
          <span>→</span>
          <input type="date" className="border rounded px-2 py-1"
            value={end} onChange={(e) => setEnd(e.target.value)} />
        </div>
      )}
    </div>
  );
}

function DevisStatusDonutCard({ stats, onNavigate, toolbar }) {
  const COLORS = {
    pending: "#CBD5E1", // slate-300 (gris)
    accepted: "#3B82F6", // blue-500 (bleu)
    abandoned: "#EF4444", // red-500 (rouge)
  };

  const chartData = stats.data.filter(d => d.value > 0);
  const empty = stats.total === 0;

  return (
    <div className="rounded-xl bg-white shadow-sm border p-5">
      <div className="flex items-start justify-between mb-3">
        <h2 className="text-xl font-semibold text-slate-900">Devis par statut</h2>
      </div>
      {toolbar}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
        {/* Donut */}
        <div className="h-[220px]">
          {empty ? (
            <div className="h-full flex items-center justify-center text-slate-400">
              Aucun devis disponible
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="label"
                  innerRadius={70}
                  outerRadius={100}
                  stroke="white"
                  strokeWidth={2}
                >
                  {chartData.map((entry) => (
                    <Cell key={entry.key} fill={COLORS[entry.key]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, _name, payload) => [
                    `${value} (${payload?.payload?.pct?.toFixed(0)}%)`,
                    payload?.payload?.label,
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Centre / Total + Légende cliquable */}
        <div className="space-y-3">
          <div className="rounded-lg bg-slate-50 border p-3 text-center">
            <div className="text-xs uppercase tracking-wide text-slate-500">Total devis</div>
            <div className="text-3xl font-semibold text-slate-900">{stats.total}</div>
          </div>

          {stats.data.map((row) => (
            <button
              key={row.key}
              onClick={() => onNavigate(row.key)}
              className="w-full flex items-center justify-between gap-3 rounded-lg border hover:border-slate-300 hover:bg-slate-50 p-3 transition"
            >
              <div className="flex items-center gap-3">
                <span
                  className="inline-block w-3 h-3 rounded-full"
                  style={{
                    background:
                      row.key === "pending" ? "#CBD5E1" :
                        row.key === "accepted" ? "#3B82F6" : "#EF4444",
                  }}
                />
                <span className="text-sm text-slate-800">{row.label}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-900">{row.value}</span>
                <span className="text-xs text-slate-500">{row.pct.toFixed(0)}%</span>
                <svg width="16" height="16" viewBox="0 0 24 24" className="text-slate-400">
                  <path fill="currentColor" d="M9 18l6-6-6-6v12z" />
                </svg>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
function FactureStatusDonutCard({ stats, onNavigate, toolbar }) {
  const COLORS = { pending: "#CBD5E1", accepted: "#10B981", abandoned: "#EF4444" }; const chartData = stats.data.filter(d => d.value > 0);
  const empty = stats.total === 0;

  return (
    <div className="rounded-xl bg-white shadow-sm border p-5">
      <div className="flex items-start justify-between">
        <h2 className="text-xl font-semibold text-slate-900">Factures par statut</h2>
      </div>

      {/* Toolbar discrète au-dessus du graphe */}
      {toolbar}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
        {/* Donut */}
        <div className="h-[220px]">
          {empty ? (
            <div className="h-full flex items-center justify-center text-slate-400">
              Aucune facture disponible
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="label"
                  innerRadius={70}
                  outerRadius={100}
                  stroke="white"
                  strokeWidth={2}
                  isAnimationActive
                >
                  {chartData.map((e) => <Cell key={e.key} fill={COLORS[e.key]} />)}
                </Pie>
                <Tooltip
                  formatter={(value, _name, p) => [
                    `${value} (${p?.payload?.pct?.toFixed(0)}%)`,
                    p?.payload?.label,
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Total + lignes cliquables */}
        <div className="space-y-3">
          <div className="rounded-lg bg-slate-50 border p-3 text-center">
            <div className="text-xs uppercase tracking-wide text-slate-500">Total factures</div>
            <div className="text-3xl font-semibold text-slate-900">{stats.total}</div>
          </div>
          {stats.data.map((row) => (
            <button
              key={row.key}
              onClick={() => onNavigate(row.key)}
              className="w-full flex items-center justify-between gap-3 rounded-lg border hover:border-slate-300 hover:bg-slate-50 p-3 transition"
            >
              <div className="flex items-center gap-3">
                <span className="inline-block w-3 h-3 rounded-full"
                  style={{ background: COLORS[row.key] }} />
                <span className="text-sm text-slate-800">
                  {row.key === "pending" ? "En attente" : row.key === "accepted" ? "Payées" : "En retard"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-900">{row.value}</span>
                <span className="text-xs text-slate-500">{row.pct.toFixed(0)}%</span>
                <svg width="16" height="16" viewBox="0 0 24 24" className="text-slate-400"><path fill="currentColor" d="M9 18l6-6-6-6v12z" /></svg>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ================= Page ================= */
export default function Home() {
  const navigate = useNavigate();

  // --- hooks de base
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [devis, setDevis] = useState([]);
  const [factures, setFactures] = useState([]);
  const [search, setSearch] = useState("");
  // --- bornes temporelles : DOIVENT être définies avant de servir ailleurs
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const yearStart = new Date(now.getFullYear(), 0, 1);
  const nextYear = new Date(now.getFullYear() + 1, 0, 1);
  // on garde le toggle CA TTC / CA HT
  const [salesMode, setSalesMode] = useState("ttc");
  // Marge brute uniquement
  const marginLevel = "brute";
  /* Affichages / filtres */
  const [range, setRange] = useState(12);
  const [customMonths, setCustomMonths] = useState(12);
  // ====== Période pour le donut Devis
  const [devisRangeMode, setDevisRangeMode] = useState("month"); // 'month' | 'year' | 'custom'
  const [devisStart, setDevisStart] = useState(() =>
    new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10)
  );
  const [devisEnd, setDevisEnd] = useState(() =>
    new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10)
  );
  const [showMargin, setShowMargin] = useState(false);
  // ====== Période pour le donut Factures (peut maintenant utiliser monthStart/nextMonth)
  const [factRangeMode, setFactRangeMode] = useState("month");
  const [factStart, setFactStart] = useState(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), 1).toISOString().slice(0, 10);
  });
  const [factEnd, setFactEnd] = useState(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth() + 1, 0).toISOString().slice(0, 10);
  });
  const { infosSociete } = useContext(SocieteContext);
  const companyName =
    infosSociete?.nom ||
    infosSociete?.societe_nom ||
    infosSociete?.raison_sociale ||
    "votre société";
  const handleSearch = (e) => {
    e?.preventDefault?.();
    const q = String(search || "").trim();
    const qs = new URLSearchParams(q ? { q } : {}).toString();
    navigate(`/documents${qs ? `?${qs}` : ""}`);
  };
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        setErr(null);
        const [rd, rf] = await Promise.all([
          axiosInstance.get("/devis"),
          axiosInstance.get("/factures"),
        ]);
        if (cancel) return;
        setDevis(extractArray(rd.data, "data", "devis", "rows"));
        setFactures(extractArray(rf.data, "data", "factures", "rows"));
      } catch (e) {
        if (cancel) return;
        console.error("Erreur chargement dashboard:", e);
        setErr({
          status: e?.response?.status,
          message: e?.response?.data?.message || e.message || "Erreur inconnue",
          endpoint: e?.config?.url,
        });
        setDevis((prev) => prev || []);
        setFactures((prev) => prev || []);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const devisInRange = useMemo(() => {
    const d0 = (s) => new Date(s);
    const inCustom = (d) =>
      d >= d0(devisStart) && d <= new Date(d0(devisEnd).getTime() + 24 * 3600 * 1000 - 1);
    return devis.filter((dv) => {
      const d = new Date(dv.date_devis || dv.created_at || Date.now());
      if (devisRangeMode === "month") return d >= monthStart && d < nextMonth;
      if (devisRangeMode === "year") return d >= yearStart && d < nextYear;
      return inCustom(d);
    });
  }, [devis, devisRangeMode, devisStart, devisEnd, monthStart, nextMonth, yearStart, nextYear]);
  const facturesInRange = useMemo(() => {
    const d0 = (s) => new Date(s);
    const inCustom = (d) => d >= d0(factStart) && d <= new Date(d0(factEnd).getTime() + 24 * 3600 * 1000 - 1);
    return factures.filter((f) => {
      const d = new Date(f.date || f.created_at || f.paid_at || Date.now());
      if (factRangeMode === "month") return d >= monthStart && d < nextMonth;
      if (factRangeMode === "year") return d >= yearStart && d < nextYear;
      return inCustom(d);
    });
  }, [factures, factRangeMode, factStart, factEnd, monthStart, nextMonth, yearStart, nextYear]);


  const facturesStats = useMemo(() => {
    const today = new Date();
    const counts = { pending: 0, accepted: 0, abandoned: 0 };
    for (const f of facturesInRange) counts[statusOfFacture(f, today)]++;

    const total = counts.pending + counts.accepted + counts.abandoned;
    const pct = (n) => (total ? (n / total) * 100 : 0);

    return {
      counts, total,
      data: [
        { key: "pending", label: "En attente", value: counts.pending, pct: pct(counts.pending) },
        { key: "accepted", label: "Payées", value: counts.accepted, pct: pct(counts.accepted) },
        { key: "abandoned", label: "En retard", value: counts.abandoned, pct: pct(counts.abandoned) },
      ],
    };
  }, [facturesInRange]);


  const devisStats = useMemo(() => {
    const counts = { pending: 0, accepted: 0, abandoned: 0 };
    for (const dv of devisInRange) counts[statusOfDevis(dv)]++;
    const total = counts.pending + counts.accepted + counts.abandoned;
    const pct = (n) => (total ? (n / total) * 100 : 0);

    return {
      counts,
      total,
      data: [
        { key: "pending", label: "En attente", value: counts.pending, pct: pct(counts.pending) },
        { key: "accepted", label: "Acceptés", value: counts.accepted, pct: pct(counts.accepted) },
        { key: "abandoned", label: "Abandonnés", value: counts.abandoned, pct: pct(counts.abandoned) },
      ],
    };
  }, [devisInRange]);

  // CA TTC Annuel / Mois (encaissé)
  const caYear = useMemo(
    () =>
      factures
        .filter(isPaid)
        .filter((f) => {
          const d = new Date(f.paid_at || f.date || f.created_at);
          return d >= yearStart && d < nextYear;
        })
        .reduce((s, f) => s + getFactureTTC(f), 0),
    [factures, yearStart, nextYear]
  );

  const caMonth = useMemo(
    () =>
      factures
        .filter(isPaid)
        .filter((f) => {
          const d = new Date(f.paid_at || f.date || f.created_at);
          return d >= monthStart && d < nextMonth;
        })
        .reduce((s, f) => s + getFactureTTC(f), 0),
    [factures, monthStart, nextMonth]
  );

  const devisMonthTTC = useMemo(
    () =>
      devis
        .filter((d) => {
          const dd = new Date(d.date_devis || d.created_at);
          return dd >= monthStart && dd < nextMonth;
        })
        .reduce((s, d) => s + clampNb(d.total_ttc ?? 0), 0),
    [devis, monthStart, nextMonth]
  );

  const docsYear = useMemo(() => {
    const devisY = devis.filter((d) => {
      const dd = new Date(d.date_devis || d.created_at);
      return dd >= yearStart && dd < nextYear;
    }).length;
    const factY = factures.filter((f) => {
      const d = new Date(f.date || f.created_at);
      return d >= yearStart && d < nextYear;
    }).length;
    return { devis: devisY, factures: factY };
  }, [devis, factures, yearStart, nextYear]);

  // KPI Marge sur le mois (toujours HT, encaissé)
  const kpiMargeMonth = useMemo(() => {
    let marge = 0;
    let caHT = 0;
    for (const f of factures) {
      if (!isPaid(f)) continue;
      const d = new Date(f.paid_at || f.date || f.created_at);
      if (d < monthStart || d >= nextMonth) continue;
      const { ht, brute } = computeMarginsHT(f);
      caHT += ht;
      marge += brute;
    }
    const pct = caHT > 0 ? (marge / caHT) * 100 : 0;
    return { marge, pct };
  }, [factures, monthStart, nextMonth, marginLevel]);

  // Win rate devis (mois en cours)
  const kpiWinRate = useMemo(() => {
    const monthDevis = devis.filter((d) => {
      const dd = new Date(d.date_devis || d.created_at);
      return dd >= monthStart && dd < nextMonth;
    });
    const accepted = monthDevis.filter((d) => normalize(d.etat ?? d.statut).includes("accept")).length;
    const rate = monthDevis.length ? (accepted / monthDevis.length) * 100 : 0;
    return { total: monthDevis.length, accepted, rate };
  }, [devis, monthStart, nextMonth]);

  /* ===== Graph CA vs Marge ===== */
  const monthsCount = range === "perso" ? Math.max(1, Number(customMonths || 1)) : Number(range);
  const seriesData = useMemo(() => {
    const keys = buildLastMonthKeys(
      range === "perso" ? Math.max(1, Number(customMonths || 1)) : Number(range),
      new Date()
    );
    const map = new Map(keys.map((k) => [k, { ca: 0, marge: 0 }]));

    for (const f of factures) {
      // Toujours bucketter sur la DATE D’ÉMISSION
      const bucketDate = (f.date || f.created_at);
      const k = monthKey(bucketDate);
      if (!map.has(k)) continue;

      // CA à afficher (TTC ou HT selon le switch)
      const { ht, brute } = computeMarginsHT(f); // marge BRUTE uniquement
      const addCA = (salesMode === "ttc") ? getFactureTTC(f) : ht;

      const row = map.get(k);
      map.set(k, {
        ca: row.ca + addCA,
        marge: row.marge + brute,
      });
    }

    return keys.map((k) => ({ key: k, label: monthLabelShort(k), ...map.get(k) }));
    // NB: plus de amountMode/marginLevel dans les deps
  }, [factures, range, customMonths, salesMode]);


  /* ===== Derniers tableaux ===== */
  const derniersDevis = useMemo(
    () =>
      [...devis]
        .sort(
          (a, b) =>
            new Date(b.date_devis || b.created_at || 0) - new Date(a.date_devis || a.created_at || 0)
        )
        .slice(0, 5),
    [devis]
  );

  const dernieresFactures = useMemo(
    () =>
      [...factures]
        .sort(
          (a, b) =>
            new Date(b.paid_at || b.date || b.created_at || 0) -
            new Date(a.paid_at || a.date || a.created_at || 0)
        )
        .slice(0, 5),
    [factures]
  );

  return (
  <div className="p-6 space-y-8">
      {/* Bandeau essai en haut de la page */}
      <TrialBanner />

{/* Bandeau d’erreur / abonnement requis */}
<ApiErrorBanner error={err} endpoint={err?.endpoint} />

      {/* ===== Titre en haut ===== */}
      <h1 className="text-2xl md:text-3xl font-bold text-slate-700 text-center">
        {companyName}
      </h1>

      {/* ===== Barre blanche avec les boutons juste en dessous ===== */}
      <div className="rounded-xl bg-white shadow-sm border p-3 mt-3">
        <div className="flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            className="h-9 px-4 rounded-full bg-sky-700 text-white hover:bg-sky-600"
            onClick={() => navigate("/creer-devis")}
          >
            Nouveau devis
          </button>
          <button
            type="button"
            className="h-9 px-4 rounded-full bg-sky-700 text-white hover:bg-sky-600"
            onClick={() => navigate("/creer-facture")}
          >
            Nouvelle facture
          </button>
        </div>
      </div>

      {/* ===== KPIs en 4 colonnes (inchangés) ===== */}
      <div className="rounded-xl bg-white shadow-sm border p-6">
        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <div className="rounded-lg border bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">CA TTC Annuel</div>
            <div className="text-2xl font-semibold mt-1">{money(caYear)}</div>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">CA TTC Mois en cours</div>
            <div className="text-2xl font-semibold mt-1">{money(caMonth)}</div>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">Marge HT Mois</div>
            <div className="text-2xl font-semibold mt-1">{money(kpiMargeMonth.marge)}</div>
            <div className="text-xs text-slate-500 mt-1">
              {kpiMargeMonth.pct.toFixed(1)}% • Brute
            </div>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">Ratios devis du mois</div>
            <div className="text-2xl font-semibold mt-1">{kpiWinRate.rate.toFixed(0)}%</div>
            <div className="text-xs text-slate-500 mt-1">{kpiWinRate.accepted} / {kpiWinRate.total} acceptés</div>
          </div>
        </div>
      </div>
      {/* ====== Donuts Statuts (alignés) ====== */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        {/* Donut Devis (inchangé, réutilise ton DevisStatusDonutCard existant) */}
        <DevisStatusDonutCard
          stats={devisStats}
          onNavigate={(key) => {
            const map = { pending: "pending", accepted: "accepted", abandoned: "abandoned" };
            const qs = new URLSearchParams({ type: "devis", statut: map[key] }).toString();
            navigate(`/documents?${qs}`);
          }}
          toolbar={
            <RangeToolbar
              mode={devisRangeMode}
              setMode={setDevisRangeMode}
              start={devisStart}
              setStart={setDevisStart}
              end={devisEnd}
              setEnd={setDevisEnd}
            />
          }
        />

        {/* Donut Factures + Toolbar période au-dessus */}
        <FactureStatusDonutCard
          stats={facturesStats}
          toolbar={
            <RangeToolbar
              mode={factRangeMode}
              setMode={setFactRangeMode}
              start={factStart}
              setStart={setFactStart}
              end={factEnd}
              setEnd={setFactEnd}
            />
          }
          onNavigate={(key) => {
            // 'paid' = accepted, 'unpaid' = pending, 'overdue' = abandoned
            const map = { pending: "unpaid", accepted: "paid", abandoned: "overdue" };
            const qs = new URLSearchParams({ type: "factures", statut: map[key] }).toString();
            navigate(`/documents?${qs}`);
          }}
        />
      </div>


      {/* ================= LIGNE 1 : 2 tableaux (on les garde) ================= */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        {/* Derniers devis */}
        <div className="rounded-xl bg-white shadow-sm border p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-semibold text-slate-900">Derniers devis</h2>
            <button className="text-sm text-sky-700 hover:underline" onClick={() => navigate("/documents")}>
              Voir tous
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b">
                  <th className="py-2 pr-3">#</th>
                  <th className="py-2 pr-3">Client</th>
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Montant</th>
                  <th className="py-2">Statut</th>
                </tr>
              </thead>
              <tbody className="text-slate-800">
                {loading ? (
                  <tr><td className="py-4 text-center" colSpan={5}>Chargement…</td></tr>
                ) : derniersDevis.length === 0 ? (
                  <tr><td className="py-4 text-center text-slate-500" colSpan={5}>Aucun devis récent.</td></tr>
                ) : (
                  derniersDevis.map((d) => (
                    <tr
                      key={`dv-${d.id}`}
                      className="border-b hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/afficher-devis?id=${d.id}`)}
                    >
                      <td className="py-2 pr-3">{d.numero_devis ? `DEV-${d.numero_devis}` : `DEV-${d.id}`}</td>
                      <td className="py-2 pr-3">{d.client_nom || "—"}</td>
                      <td className="py-2 pr-3">{dFR(d.date_devis || d.created_at)}</td>
                      <td className="py-2 pr-3">{money(d.total_ttc ?? 0)}</td>
                      <td className="py-2">
                        {(() => {
                          const s = normalize(d.etat ?? d.statut);
                          if (s.includes("brouillon")) return <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700">Brouillon</span>;
                          if (s.includes("accept")) return <span className="px-2 py-0.5 rounded text-xs bg-emerald-100 text-emerald-700">Accepté</span>;
                          if (s.includes("abandon")) return <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700">Abandonné</span>;
                          return <span className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700">En cours</span>;
                        })()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Dernières factures */}
        <div className="rounded-xl bg-white shadow-sm border p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-semibold text-slate-900">Dernières factures</h2>
            <button className="text-sm text-sky-700 hover:underline" onClick={() => navigate("/documents")}>
              Voir toutes
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b">
                  <th className="py-2 pr-3">#</th>
                  <th className="py-2 pr-3">Client</th>
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Montant</th>
                  <th className="py-2">Statut</th>
                </tr>
              </thead>
              <tbody className="text-slate-800">
                {loading ? (
                  <tr><td className="py-4 text-center" colSpan={5}>Chargement…</td></tr>
                ) : dernieresFactures.length === 0 ? (
                  <tr><td className="py-4 text-center text-slate-500" colSpan={5}>Aucune facture récente.</td></tr>
                ) : (
                  dernieresFactures.map((f) => (
                    <tr
                      key={`fa-${f.id}`}
                      className="border-b hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/afficher-facture?id=${f.id}`)}
                    >
                      <td className="py-2 pr-3">{f.numero || f.numero_facture || `FAC-${f.id}`}</td>
                      <td className="py-2 pr-3">{f.client_nom || "—"}</td>
                      <td className="py-2 pr-3">{dFR((isPaid(f) ? (f.paid_at || f.date) : (f.date)) || f.created_at)}</td>
                      <td className="py-2 pr-3">{money(getFactureTTC(f))}</td>
                      <td className="py-2">
                        {isPaid(f) ? (
                          <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">Payée</span>
                        ) : (
                          <span className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700">Envoyée</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ================= LIGNE 2 : CA vs Marge ================= */}
      <div className="rounded-xl bg-white shadow-sm border p-5">
        {/* Titre + Toolbar dans le même bloc */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-xl font-semibold text-slate-900">Chiffre d’affaires & Marge</h2>

          <div className="flex flex-wrap items-center gap-2">
            {/* Bouton afficher/masquer Marge */}
            <button
              type="button"
              onClick={() => setShowMargin((v) => !v)}
              className="px-3 h-9 text-sm rounded-full border border-slate-300 bg-white hover:bg-slate-50"
            >
              {showMargin ? "Masquer la marge" : "Afficher la marge"}
            </button>

            {/* CA: TTC / HT */}
            <div className="flex rounded-full overflow-hidden border border-slate-300">
              <button
                className={`px-3 h-9 text-sm ${salesMode === "ttc" ? "bg-sky-700 text-white" : "bg-white"}`}
                onClick={() => setSalesMode("ttc")}
              >
                CA TTC
              </button>
              <button
                className={`px-3 h-9 text-sm border-l border-slate-300 ${salesMode === "ht" ? "bg-sky-700 text-white" : "bg-white"}`}
                onClick={() => setSalesMode("ht")}
              >
                CA HT
              </button>
            </div>

            {/* Période */}
            <div className="flex items-center gap-2">
              <button
                className={`px-2 py-1 text-xs rounded border ${range === 3 ? "bg-gray-100" : "bg-white"}`}
                onClick={() => setRange(3)}
              >
                3 mois
              </button>
              <button
                className={`px-2 py-1 text-xs rounded border ${range === 6 ? "bg-gray-100" : "bg-white"}`}
                onClick={() => setRange(6)}
              >
                6 mois
              </button>
              <button
                className={`px-2 py-1 text-xs rounded border ${range === 12 ? "bg-gray-100" : "bg-white"}`}
                onClick={() => setRange(12)}
              >
                12 mois
              </button>
              <button
                className={`px-2 py-1 text-xs rounded border ${range === "perso" ? "bg-gray-100" : "bg-white"}`}
                onClick={() => setRange("perso")}
                title="Personnalisé (N derniers mois)"
              >
                Perso
              </button>
              <input
                type="number"
                min={1}
                className="w-16 border rounded px-1 py-0.5 text-xs"
                value={customMonths}
                onChange={(e) => setCustomMonths(e.target.value)}
                title="N derniers mois"
              />
            </div>
          </div>
        </div>

        {/* petit espace pour éviter que la première graduation colle au titre */}
        <div className="h-2" />

        <RevenueLineDual data={seriesData} showMargin={showMargin} />
      </div>
    </div>
  );
}
